import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, delay } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface UpgradePayload {
  file_name: string;
  signature: string;
  file_size: number;
  firmware_version: string;
  file_path_on_device: string;
  description: string;
  priority: number;
}

export interface RolloutConfigPayload {
  exponentialRate: {
    baseRatePerMinute: number;
    incrementFactor: number;
    rateIncreaseCriteria: {
      numberOfSucceededThings: number;
    };
  };
  maximumPerMinute: number;
}

export interface AbortConfigPayload {
  criteriaList: Array<{
    action: string;
    failureType: string;
    minNumberOfExecutedThings: number;
    thresholdPercentage: number;
  }>;
}

export interface JobCreationData {
  job_name: string;
  model: string;
  actual_model: string;
  mb_upgrade: UpgradePayload;
  wm_upgrade: UpgradePayload;
  job_executions_rollout_config: RolloutConfigPayload;
  abort_config: AbortConfigPayload;
  inprogress_timeout_in_minutes: number;
}

export interface JobCreationFormData {
  // Basic Details
  job_name: string;
  model: string;
  actual_model: string;
  job_type: string;
  upgrade_type: string;
  region: string;
  inprogress_timeout_in_minutes: number;

  // Wi-Fi Board Firmware
  // wifi_firmware_name?: string;
  wifi_firmware_version?: string;
  wifi_firmware_path_on_device?: string;
  wifi_firmware_priority?: string;
  wifi_firmware_description?: string;

  // Main Board Firmware
  // main_firmware_name?: string;
  main_firmware_version?: string;
  main_firmware_path_on_device?: string;
  main_firmware_priority?: string;
  main_firmware_description?: string;

  // Configurations
  job_executions_rollout_config?: {
    baseRatePerMinute: number;
    incrementFactor: number;
    numberOfSucceededThings: number;
    maximumPerMinute: number;
  };
  abort_config?: {
    configurations: Array<{
      action: string;
      failureType: string;
      minExecutedThings: number;
      thresholdPercentage: number;
    }>;
  };
}

export interface DropdownOption {
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root'
})
export class OTAJobCreationService {

  constructor(private http: HttpClient,) { }

  /**
   * Get available models
   */
  getModels(): Observable<DropdownOption[]> {
    const models: DropdownOption[] = [
      { label: 'EssMBAC', value: 'EssMBAC' },
      { label: 'EssMBAC_010004', value: 'EssMBAC_010004' },
      { label: 'CO-IAQ', value: 'CO-IAQ' },
      { label: 'COPD-WF/AQ', value: 'COPD-WF/AQ' },
      { label: 'WD-ESP32', value: 'WD-ESP32' },
      { label: 'WD-ESP32-IAQ', value: 'WD-ESP32-IAQ' }
    ];
    return of(models).pipe(delay(500));
  }

  getActualModels(): Observable<DropdownOption[]> {
    const models: DropdownOption[] = [
      { label: 'WD', value: 'WD' },
      { label: 'WD-IAQ', value: 'WD-IAQ' },
      { label: 'WD-ESP32-CO-IAQ', value: 'WD-ESP32-CO-IAQ' },
      { label: 'WLD', value: 'WLD' },
      { label: 'CO-IAQ', value: 'CO-IAQ' },
      { label: 'WD-ESP32-IAQ', value: 'WD-ESP32-IAQ' },
      { label: 'WD-ESP32', value: 'WD-ESP32' },
      { label: 'WLD-TEST', value: 'WLD-TEST' },
      { label: 'CO2D', value: 'CO2D' },
      { label: 'EssWFAC', value: 'EssWFAC' },
      { label: 'EssWFDC', value: 'EssWFDC' },
      { label: 'COPD-WF/AQ', value: 'COPD-WF/AQ' },
    ];
    return of(models).pipe(delay(500));
  }

  /**
   * Get job types
   */
  getJobTypes(): Observable<DropdownOption[]> {
    const types: DropdownOption[] = [
      { label: 'Custom', value: 'custom' },
      { label: 'Standard', value: 'standard' }
    ];
    return of(types).pipe(delay(500));
  }

  /**
   * Get upgrade types
   */
  getUpgradeTypes(): Observable<DropdownOption[]> {
    const types: DropdownOption[] = [
      { label: 'User Consent', value: 'user-consent' },
      { label: 'Force', value: 'force' },
      { label: 'Scheduled', value: 'scheduled' }
    ];
    return of(types).pipe(delay(500));
  }

  /**
   * Get regions
   */
  getRegions(): Observable<DropdownOption[]> {
    const regions: DropdownOption[] = [
      { label: 'US - East', value: 'us-east-1' },
      { label: 'US - West', value: 'us-west-2' },
      { label: 'EU - West', value: 'eu-west-1' },
      { label: 'AP - Southeast', value: 'ap-southeast-1' }
    ];
    return of(regions).pipe(delay(500));
  }

  /**
   * Create a new job
   */
  createJob(jobData: JobCreationData): Observable<any> {
    // console.log('Creating job with data:', jobData);
    // // Replace with actual API call
    // return of({ id: 1, message: 'Job created successfully' }).pipe(delay(2000));

    const apiUrl = `${environment.host}/ota`;
    return this.http.post(apiUrl, jobData);
  }

  /**
   * Validate job data
   */
  validateJobData(jobData: Partial<JobCreationFormData>): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!jobData.job_name || jobData.job_name.trim() === '') {
      errors.push('Job Name is required');
    }
    if (!jobData.model) {
      errors.push('Model is required');
    }
    // if (!jobData.job_type) {
    //   errors.push('Job Type is required');
    // }
    // if (!jobData.upgrade_type) {
    //   errors.push('Upgrade Type is required');
    // }
    // if (!jobData.region) {
    //   errors.push('Region is required');
    // }
    if (!jobData.inprogress_timeout_in_minutes || jobData.inprogress_timeout_in_minutes <= 0) {
      errors.push('In Progress Timeout must be greater than 0');
    }

    return {
      valid: errors.length === 0,
      errors
    };
  }
}